import { useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import logo from '../logo2.svg';

// Pagină publică (fără login) cu informații legale: GDPR/Privacy, Cookies, Terms, About.
// Accesibilă oricând prin /legal/:tab. Folosit standard pe alte platforme (Stripe, Notion etc).
const Legal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tab } = useParams();
  const { isAuthenticated } = useAuth();
  const validTabs = ['privacy', 'cookies', 'terms', 'about', 'contact'];
  const activeTab = validTabs.includes(tab) ? tab : 'privacy';

  useEffect(() => {
    if (!validTabs.includes(tab)) navigate('/legal/privacy', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const tabs = [
    { id: 'privacy', label: t('legal.privacy') },
    { id: 'cookies', label: t('legal.cookies') },
    { id: 'terms', label: t('legal.terms') },
    { id: 'about', label: t('legal.about') },
    { id: 'contact', label: t('legal.contact') },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header simplu, accesibil fără login */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to={isAuthenticated ? '/dashboard' : '/login'} className="flex items-center gap-3">
            <img src={logo} alt="SSMPlatform" className="h-12 w-auto" />
          </Link>
          <Link
            to={isAuthenticated ? '/dashboard' : '/login'}
            className="text-sm text-primary-600 dark:text-primary-300 hover:underline"
          >
            {isAuthenticated ? t('legal.backToApp') : t('legal.backToLogin')}
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="border-b border-slate-200 dark:border-slate-700 mb-6">
          <nav className="-mb-px flex flex-wrap gap-x-6">
            {tabs.map((tb) => (
              <button
                key={tb.id}
                onClick={() => navigate(`/legal/${tb.id}`)}
                className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === tb.id
                    ? 'border-primary-600 text-primary-700 dark:text-primary-300 dark:border-primary-400'
                    : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                }`}
              >
                {tb.label}
              </button>
            ))}
          </nav>
        </div>

        <article className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-6 lg:p-10">
          {activeTab === 'privacy' && <PrivacyContent />}
          {activeTab === 'cookies' && <CookiesContent />}
          {activeTab === 'terms' && <TermsContent />}
          {activeTab === 'about' && <AboutContent />}
          {activeTab === 'contact' && <ContactContent />}
        </article>

        <p className="mt-6 text-xs text-slate-500 dark:text-slate-400 text-center">
          {t('legal.lastUpdated')}: 2026-07-08 · Versiune documente: 2.0
        </p>
      </div>
    </div>
  );
};

const PrivacyContent = () => (
  <div className="prose prose-slate dark:prose-invert max-w-none text-sm text-slate-700 dark:text-slate-300 space-y-4">
    <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
      Politica de Confidențialitate
    </h1>
    <p className="text-slate-500 dark:text-slate-400">
      <strong>Versiune:</strong> 2.0 &nbsp;·&nbsp; <strong>Data intrării în vigoare:</strong>{' '}
      08.07.2026
    </p>
    <p className="text-slate-500 dark:text-slate-400 italic">
      Bază legală: Regulamentul UE 2016/679 (GDPR), Legea 190/2018, Legea 506/2004.
    </p>

    <Section title="Preambul">
      <p>
        Această Politică explică modul în care SSMPlatform prelucrează datele cu caracter personal
        ale utilizatorilor. Este redactată conform Art. 13 și 14 GDPR și are scopul de a vă oferi
        toate informațiile necesare pentru a înțelege și a vă exercita drepturile.
      </p>
    </Section>

    <Section title="1. Operatorii de date și rolurile lor">
      <p>
        Arhitectura Platformei implică{' '}
        <strong>mai mulți operatori de date cu roluri distincte</strong>:
      </p>
      <h3 className="font-semibold mt-3">
        1.1. Operatorul Platformei (Furnizor SaaS) — persoană împuternicită Art. 28 GDPR
      </h3>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          <strong>Denumire:</strong> RAZI TECH S.R.L.
        </li>
        <li>
          <strong>Sediu:</strong> Jud. Brașov, Municipiul Brașov, Strada Jupiter, Nr. 4, Bl. 146,
          Scara A, Ap. 15
        </li>
        <li>
          <strong>CUI:</strong> 50690233
        </li>
        <li>
          <strong>Nr. Reg. Com.:</strong> J2024030566002
        </li>
        <li>
          <strong>E-mail oficial:</strong> contact@razitech.ro
        </li>
        <li>
          <strong>Rol:</strong> Furnizează infrastructura și prelucrează datele exclusiv în baza
          instrucțiunilor scrise ale firmelor Client (operatori), conform Acordului de prelucrare a
          datelor (DPA) Art. 28 GDPR semnat cu fiecare Client.
        </li>
      </ul>
      <h3 className="font-semibold mt-3">
        1.2. Firma angajatoare (Client) — operator independent Art. 4 pct. 7 GDPR
      </h3>
      <p>
        Firma la care lucrați este <strong>operatorul principal</strong> al datelor personale ale
        angajaților săi. Ea stabilește scopurile și mijloacele prelucrării, în vederea îndeplinirii
        obligațiilor sale legale (raport de muncă, SSM, fiscalitate etc.).
      </p>
      <h3 className="font-semibold mt-3">
        1.3. Firma SSM externă (dacă există) — operator distinct sau împuternicit
      </h3>
      <p>
        Dacă angajatorul folosește serviciile externalizate ale unei firme SSM, aceasta poate
        acționa fie ca operator distinct (când prelucrează datele în temeiul propriei obligații
        legale ca prestator SSM), fie ca persoană împuternicită a angajatorului. Calificarea
        concretă rezultă din contractul de prestări servicii SSM dintre angajator și firma SSM.
      </p>
    </Section>

    <Section title="2. Responsabilul cu Protecția Datelor (DPO)">
      <p>
        Operatorul Platformei a desemnat un Responsabil cu Protecția Datelor (Data Protection
        Officer) conform Art. 37 GDPR și Legii 190/2018:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          <strong>Nume:</strong> -
        </li>
        <li>
          <strong>E-mail:</strong> contact@razitech.ro
        </li>
        <li>
          <strong>Telefon:</strong> -
        </li>
      </ul>
      <p>
        DPO este punctul vostru de contact pentru orice întrebare privind prelucrarea datelor și
        pentru exercitarea drepturilor.
      </p>
    </Section>

    <Section title="3. Categoriile de date prelucrate">
      <h3 className="font-semibold mt-3">3.1. Date de identificare</h3>
      <ul className="list-disc pl-5 space-y-1">
        <li>Nume, prenume</li>
        <li>E-mail (de regulă cel profesional)</li>
        <li>Telefon mobil</li>
        <li>Limba preferată (RO/EN)</li>
      </ul>
      <h3 className="font-semibold mt-3">3.2. Cod Numeric Personal (CNP) — REGIM SPECIAL</h3>
      <p>
        <strong>Atenție:</strong> Conform <strong>Art. 4 alin. (1) din Legea 190/2018</strong>, CNP
        este un identificator național cu protecție specială. Prelucrarea CNP-ului în Platformă este
        permisă pe baza:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          obligației legale de identificare unică în fișele individuale de instruire (Anexa 11 din
          HG 1425/2006);
        </li>
        <li>executării contractului individual de muncă.</li>
      </ul>
      <p className="mt-2">
        <strong>Măsuri specifice CNP:</strong>
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          stocat criptat AES-256-GCM la nivel de coloană (nu apare niciodată în clar în baza de
          date);
        </li>
        <li>accesul la decriptare este auditat;</li>
        <li>niciodată afișat în întregime în UI, în URL-uri, în loguri sau în notificări;</li>
        <li>
          nu este transferat către terți decât conform obligațiilor legale (ITM la solicitare,
          autorități judiciare).
        </li>
      </ul>
      <h3 className="font-semibold mt-3">3.3. Date privind situația familială și domiciliul</h3>
      <ul className="list-disc pl-5 space-y-1">
        <li>Adresa de domiciliu (necesară pentru evaluarea traseului casă-serviciu)</li>
        <li>Adresa locului de muncă declarat</li>
        <li>Traseul zilnic casă-serviciu (Art. 5 alin. (1) lit. g din HG 1425/2006)</li>
        <li>Mijlocul de transport utilizat</li>
        <li>Data și locul nașterii (opțional)</li>
      </ul>
      <h3 className="font-semibold mt-3">3.4. Date profesionale</h3>
      <ul className="list-disc pl-5 space-y-1">
        <li>Funcția, poziția în organigramă, departamentul</li>
        <li>Data angajării</li>
        <li>Calificare, autorizații speciale, legitimații</li>
        <li>Marcă/număr legitimație</li>
        <li>Echipament individual de protecție (EPI) atribuit</li>
      </ul>
      <h3 className="font-semibold mt-3">
        3.5. Date privind sănătatea — CATEGORIE SPECIALĂ Art. 9 GDPR
      </h3>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          <strong>Grupa sanguină</strong> (opțional, util în caz de accident de muncă)
        </li>
        <li>
          <strong>Certificatele medicale uploadate</strong> (PDF) — apt/inapt de muncă, examen
          medical periodic
        </li>
      </ul>
      <p className="mt-2">
        <strong>Bază legală pentru categoria specială (Art. 9 alin. 2 lit. b GDPR):</strong>{' '}
        „necesitatea în scopuri legate de obligațiile și drepturile specifice ale operatorului sau
        ale persoanei vizate în domeniul dreptului muncii, securității sociale și protecției
        sociale". Coroborat cu Legea 418/2004 și HG 355/2007 privind supravegherea sănătății
        lucrătorilor.
      </p>
      <h3 className="font-semibold mt-3">3.6. Date privind instruirea profesională</h3>
      <ul className="list-disc pl-5 space-y-1">
        <li>Cursurile atribuite și starea acestora (în curs / finalizat)</li>
        <li>Răspunsurile la întrebările interactive</li>
        <li>Scorul obținut la testele finale</li>
        <li>Certificatele și fișele individuale generate</li>
        <li>Istoricul instructajelor periodice (IP)</li>
      </ul>
      <h3 className="font-semibold mt-3">3.7. Semnătura electronică</h3>
      <ul className="list-disc pl-5 space-y-1">
        <li>Imaginea grafică a semnăturii desenate de Utilizator</li>
        <li>
          Cheia criptografică AdES (RSA-2048) — cheie privată criptată cu o parolă derivată din
          parola Utilizatorului
        </li>
        <li>Recovery key pentru cazul resetării parolei</li>
        <li>Marcă temporală (timestamp) pentru fiecare semnătură aplicată</li>
      </ul>
      <p className="mt-2">
        <strong>Bază legală:</strong> Regulamentul (UE) 910/2014 (eIDAS), Legea 455/2001.
      </p>
      <h3 className="font-semibold mt-3">3.8. Date tehnice (audit și securitate)</h3>
      <ul className="list-disc pl-5 space-y-1">
        <li>Adresa IP</li>
        <li>User-Agent (tip browser, sistem operare)</li>
        <li>
          Data și ora fiecărei acțiuni semnificative (login, semnătură, modificare date, descărcare
          PDF)
        </li>
        <li>
          Sesiune de impersonare (când un SUPER_ADMIN se autentifică ca un alt utilizator, cu
          acordul acestuia)
        </li>
      </ul>
    </Section>

    <Section title="4. Scopurile prelucrării și temeiul juridic">
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs border border-slate-200 dark:border-slate-700">
          <thead>
            <tr className="bg-slate-100 dark:bg-slate-700">
              <th className="border border-slate-200 dark:border-slate-600 px-2 py-1 text-left">
                Scop
              </th>
              <th className="border border-slate-200 dark:border-slate-600 px-2 py-1 text-left">
                Temei juridic GDPR
              </th>
              <th className="border border-slate-200 dark:border-slate-600 px-2 py-1 text-left">
                Referință legală RO
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Generarea fișelor individuale de instruire SSM
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Art. 6 (1) lit. c — obligație legală
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Legea 319/2006 Art. 20, HG 1425/2006 Art. 81
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Organizarea instruirii pentru situații de urgență (SU)
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Art. 6 (1) lit. c — obligație legală
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Legea 307/2006, HG 1739/2006
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Emiterea certificatelor de absolvire
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Art. 6 (1) lit. b — executarea contractului
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Codul Muncii
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Evidența admiterilor la lucru și a IP-urilor
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Art. 6 (1) lit. c — obligație legală
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                HG 1425/2006 Anexa 11
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Raportare către ITM
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Art. 6 (1) lit. c — obligație legală
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Legea 108/1999, OG 95/2003
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Prelucrare CNP
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Art. 6 lit. c + Legea 190/2018 Art. 4
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                HG 1425/2006
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Prelucrare date sănătate
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Art. 9 alin. (2) lit. b GDPR
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                HG 355/2007, Legea 418/2004
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Securitatea Platformei (audit log, IP)
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Art. 6 (1) lit. f — interes legitim
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                GDPR considerentul 49
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Trimitere notificări de IP expirat
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Art. 6 (1) lit. c
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                HG 1425/2006
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Funcționalități opționale (preferințe UI, mod întunecat)
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Art. 6 (1) lit. a — consimțământ
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">—</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Section>

    <Section title="5. Decizii automatizate și profilare (Art. 22 GDPR)">
      <p>
        Platforma utilizează <strong>un singur tip de decizie automatizată cu efect juridic</strong>
        : scorul automat la testul final de evaluare. După parcurgerea cursului, Utilizatorul
        susține un test grilă, iar sistemul calculează automat scorul. Dacă scorul depășește pragul
        stabilit (de regulă 70%), Utilizatorul este considerat „APT" și se generează certificatul;
        altfel trebuie să refacă testul.
      </p>
      <p className="mt-2">
        <strong>Drept de contestare.</strong> Utilizatorul are dreptul:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          să conteste rezultatul automat — instructorul SSM uman revizuiește testul și poate corecta
          scorul;
        </li>
        <li>să ceară intervenția umană înainte ca decizia să producă efecte definitive;</li>
        <li>să își exprime punctul de vedere asupra rezultatului.</li>
      </ul>
      <p className="mt-2">
        <strong>Profilare:</strong> Platforma nu realizează profilare comportamentală. Datele de
        audit log sunt folosite exclusiv pentru securitate.
      </p>
    </Section>

    <Section title="6. Destinatarii datelor">
      <h3 className="font-semibold mt-3">6.1. Destinatari interni</h3>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          Personalul angajatorului cu rol funcțional (HR, COMPANY_ADMIN, DIRECTOR_GENERAL,
          conducător loc de muncă — CLM)
        </li>
        <li>Personalul firmei SSM externe atribuit (SSM_COMPANY_ADMIN, SSM_INSTRUCTOR)</li>
      </ul>
      <h3 className="font-semibold mt-3">6.2. Destinatari externi — obligație legală</h3>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          Inspecția Teritorială de Muncă (ITM) — la solicitare scrisă, în control sau accident de
          muncă
        </li>
        <li>ISU — pentru fișele de instruire PSI</li>
        <li>Casa de Asigurări de Sănătate — în caz de boală profesională</li>
        <li>Organele judiciare — în baza unei hotărâri sau cereri legale</li>
        <li>ANSPDCP — în caz de control sau plângere</li>
      </ul>
      <h3 className="font-semibold mt-3">6.3. Persoane împuternicite (subprestatori Art. 28)</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs border border-slate-200 dark:border-slate-700">
          <thead>
            <tr className="bg-slate-100 dark:bg-slate-700">
              <th className="border border-slate-200 dark:border-slate-600 px-2 py-1 text-left">
                Categorie
              </th>
              <th className="border border-slate-200 dark:border-slate-600 px-2 py-1 text-left">
                Furnizor
              </th>
              <th className="border border-slate-200 dark:border-slate-600 px-2 py-1 text-left">
                Locație
              </th>
              <th className="border border-slate-200 dark:border-slate-600 px-2 py-1 text-left">
                Scop
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Hosting infrastructură
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Hetzner Online GmbH
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">UE</td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Stocare bază de date, fișiere
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                E-mail tranzacțional
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">Resend</td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">UE</td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Trimitere notificări sistem
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Backup off-site
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Hetzner Online GmbH
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">UE</td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Recuperare în caz de dezastru
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-2">
        Lista actualizată a subprestatorilor este publicată la <code>/legal/subprocessors</code>{' '}
        (sau în Anexa la DPA). Adăugarea unui nou subprestator se notifică Clientului cu 30 zile în
        avans, cu drept de opoziție.
      </p>
      <h3 className="font-semibold mt-3">6.4. Datele NU sunt:</h3>
      <ul className="list-disc pl-5 space-y-1">
        <li>vândute către terți;</li>
        <li>folosite în scopuri de marketing;</li>
        <li>accesate de companii de publicitate sau analytics comportamental.</li>
      </ul>
    </Section>

    <Section title="7. Transferuri internaționale">
      <p>
        Datele sunt stocate exclusiv în Uniunea Europeană (Hetzner Online GmbH). Nu se efectuează
        transferuri către state din afara UE/SEE. În cazul în care, în viitor, Operatorul va folosi
        servicii care implică transferuri extra-UE, acestea vor fi protejate prin Clauzele
        Contractuale Standard (SCC) și prin Transfer Impact Assessment (TIA) conform jurisprudenței
        Schrems II, iar Politica va fi actualizată corespunzător.
      </p>
    </Section>

    <Section title="8. Perioada de stocare">
      <h3 className="font-semibold mt-3">
        8.1. Date de instruire SSM (fișe, certificate, semnături)
      </h3>
      <p>
        Pe toată durata raportului de muncă <strong>+ 10 ani</strong> după încetare, pentru a
        permite probarea în cazul unei eventuale acțiuni în răspundere civilă (Art. 2.518 Cod Civil
        — termen general de prescripție pentru obligații izvorâte din muncă).
      </p>
      <h3 className="font-semibold mt-3">8.2. Date privind expunerea la agenți specifici</h3>
      <p>
        <strong>40 de ani</strong> după încetarea expunerii, dacă raportul de muncă a implicat
        expunere la:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>agenți cancerigeni sau mutageni (HG 1093/2006 Art. 16)</li>
        <li>azbest (HG 1875/2005)</li>
        <li>agenți biologici grupa 3 sau 4 (HG 1092/2006)</li>
        <li>radiații ionizante (Ordin CNCAN 14/2000)</li>
      </ul>
      <p>
        În aceste cazuri, fișa de instruire este parte din dosarul medical al expunerii și nu poate
        fi distrusă mai devreme.
      </p>
      <h3 className="font-semibold mt-3">8.3. Certificate medicale și examene periodice</h3>
      <p>
        Conform HG 355/2007 Art. 30 — păstrate de medicul de medicina muncii. Pe Platformă, copia
        este stocată pe durata raportului de muncă + 10 ani.
      </p>
      <h3 className="font-semibold mt-3">8.4. Cont utilizator</h3>
      <p>
        Pe durata existenței contului. La ștergere, datele de identificare se anonimizează,
        păstrându-se doar referințe pseudonimizate către fișele de instruire (cu ID intern, fără CNP
        în clar).
      </p>
      <h3 className="font-semibold mt-3">8.5. Audit log</h3>
      <p>
        <strong>5 ani</strong> de la generarea evenimentului. Excepție: anchetă în curs (penală,
        disciplinară, sancțiune ITM) — retenția se extinde până la finalizare.
      </p>
      <h3 className="font-semibold mt-3">8.6. Date de logare invalide</h3>
      <p>
        <strong>90 zile</strong>, pentru detectare tentative de breach.
      </p>
      <h3 className="font-semibold mt-3">8.7. Backup-uri</h3>
      <p>
        Copiile zilnice se rotesc: retenție 30 zile pentru backup standard, 1 an pentru backup lunar
        arhivat.
      </p>
    </Section>

    <Section title="9. Securitatea datelor (Art. 32 GDPR)">
      <h3 className="font-semibold mt-3">9.1. Măsuri tehnice</h3>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          Criptare: AES-256-GCM pentru CNP la nivel de coloană; HTTPS/TLS 1.3 în tranzit; backup-uri
          criptate; chei AdES protejate cu parola Utilizatorului.
        </li>
        <li>Hash parole: bcrypt cost 12.</li>
        <li>Autentificare: token JWT cu expirare 8h, rate limit 5 login-uri/15 min.</li>
        <li>
          Control acces: ierarhie de roluri pe 6 niveluri + sub-flag-uri; izolare multi-tenant
          strictă.
        </li>
        <li>Audit log al fiecărei acțiuni sensibile.</li>
        <li>Anti-brute-force: rate limit + lockout temporar.</li>
        <li>Backup zilnic, retenție 30 zile, restore test trimestrial.</li>
      </ul>
      <h3 className="font-semibold mt-3">9.2. Măsuri organizaționale</h3>
      <ul className="list-disc pl-5 space-y-1">
        <li>Politica de acces strict pe nevoie de a cunoaște (need-to-know).</li>
        <li>Training intern al personalului cu acces la date.</li>
        <li>Procedura de notificare incident securitate (72 h conform Art. 33 GDPR).</li>
        <li>DPA semnat cu toți subprestatorii (Art. 28 GDPR).</li>
      </ul>
      <h3 className="font-semibold mt-3">9.3. Notificare breach</h3>
      <ul className="list-disc pl-5 space-y-1">
        <li>ANSPDCP este notificată în maxim 72 h (Art. 33 GDPR);</li>
        <li>persoanele vizate sunt notificate dacă există risc ridicat (Art. 34 GDPR);</li>
        <li>Clienții afectați sunt notificați imediat ce situația este cunoscută.</li>
      </ul>
    </Section>

    <Section title="10. Drepturile dumneavoastră">
      <p>
        În calitate de persoană vizată, aveți următoarele drepturi conform GDPR (Capitolul III) și
        Legii 190/2018:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          <strong>Dreptul de acces (Art. 15)</strong> — copie a datelor; răspuns gratuit în maxim 30
          zile (extinsibil la 90).
        </li>
        <li>
          <strong>Dreptul la rectificare (Art. 16)</strong> — corectarea datelor inexacte; pentru
          date sensibile (CNP) prin administrator sau DPO.
        </li>
        <li>
          <strong>Dreptul la ștergere (Art. 17)</strong> — în limitele retenției legale; datele de
          instruire SSM nu pot fi șterse pe durata retenției, dar pot fi anonimizate.
        </li>
        <li>
          <strong>Dreptul la restricționare (Art. 18)</strong>.
        </li>
        <li>
          <strong>Dreptul la portabilitate (Art. 20)</strong> — copie în format structurat
          (JSON/CSV).
        </li>
        <li>
          <strong>Dreptul la opoziție (Art. 21)</strong> — pentru prelucrările bazate pe interes
          legitim.
        </li>
        <li>
          <strong>Dreptul de retragere a consimțământului (Art. 7 alin. 3)</strong> — pentru
          prelucrările bazate pe consimțământ.
        </li>
        <li>
          <strong>Drepturi legate de deciziile automatizate (Art. 22)</strong> — vezi secțiunea 5.
        </li>
      </ul>
      <p className="mt-2">
        <strong>Dreptul de a depune plângere la autoritate:</strong>
      </p>
      <p>
        Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal (ANSPDCP),
        B-dul G-ral. Gheorghe Magheru nr. 28-30, Sector 1, București. E-mail:
        anspdcp@dataprotection.ro. Website:{' '}
        <a
          href="https://www.dataprotection.ro"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:underline"
        >
          www.dataprotection.ro
        </a>
      </p>
      <p className="mt-2">
        <strong>Modul de exercitare:</strong> cererile se transmit la contact@razitech.ro sau în
        scris la sediul Operatorului. Termen de răspuns: 30 zile (extinsibil la 90 pentru cereri
        complexe). Gratuit, cu excepția cererilor vădit nefondate sau excesive (Art. 12 alin. 5).
      </p>
    </Section>

    <Section title="11. Cookies și tehnologii de stocare locală">
      <p>Detalii în Politica de Cookies (tabul „Cookies").</p>
    </Section>

    <Section title="12. Date privind minorii">
      <p>
        Platforma este destinată exclusiv mediului profesional, iar utilizarea ei presupune
        existența unui raport de muncă valabil. Nu prelucrăm date despre minori sub 16 ani. Dacă
        identificăm că un cont aparține unui minor, acesta va fi suspendat imediat.
      </p>
    </Section>

    <Section title="13. Modificarea Politicii">
      <p>
        Operatorul își rezervă dreptul de a actualiza această Politică. Modificările sunt afișate cu
        data actualizării, versionate intern (câmpul <code>gdprVersion</code>); la modificări
        substanțiale, Utilizatorul este rugat să accepte din nou la următoarea conectare. Versiunea
        anterioară este disponibilă la cerere.
      </p>
    </Section>

    <Section title="14. Contact">
      <ul className="list-disc pl-5 space-y-1">
        <li>
          <strong>DPO:</strong> contact@razitech.ro
        </li>
        <li>
          <strong>Suport general:</strong> contact@razitech.ro
        </li>
        <li>
          <strong>Adresă poștală:</strong> Jud. Brașov, Municipiul Brașov, Strada Jupiter, Nr. 4,
          Bl. 146, Scara A, Ap. 15
        </li>
        <li>
          <strong>ANSPDCP:</strong> www.dataprotection.ro
        </li>
      </ul>
    </Section>
  </div>
);

const CookiesContent = () => (
  <div className="prose prose-slate dark:prose-invert max-w-none text-sm text-slate-700 dark:text-slate-300 space-y-4">
    <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
      Politica de Cookies și Tehnologii de Stocare Locală
    </h1>
    <p className="text-slate-500 dark:text-slate-400">
      <strong>Versiune:</strong> 2.0 &nbsp;·&nbsp; <strong>Data intrării în vigoare:</strong>{' '}
      08.07.2026
    </p>
    <p className="text-slate-500 dark:text-slate-400 italic">
      Bază legală: Legea 506/2004 (Art. 4 alin. 5), Directiva 2002/58/CE (ePrivacy), Regulamentul UE
      2016/679.
    </p>

    <Section title="1. Ce sunt cookies și tehnologiile asociate">
      <p>
        <strong>Cookies</strong> sunt fișiere text de mici dimensiuni stocate de browser pe
        dispozitivul dumneavoastră la accesarea unui site web. Pe lângă cookies, browserele moderne
        permit și alte tehnologii echivalente — <strong>localStorage</strong>,{' '}
        <strong>sessionStorage</strong>, <strong>IndexedDB</strong>. Conform jurisprudenței europene
        (CJUE C-673/17 Planet49, 2019), toate aceste tehnologii sunt supuse acelorași reguli ca și
        cookies-urile clasice.
      </p>
    </Section>

    <Section title="2. Categorii de cookies / storage utilizate">
      <p>Conform Recomandării EDPB 2/2019, cookies se împart în două mari categorii:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          <strong>Strict necesare (esențiale):</strong> Nu necesită consimțământ. Sunt
          indispensabile pentru funcționarea serviciului solicitat de utilizator.
        </li>
        <li>
          <strong>Non-esențiale (analitice, marketing, preferințe non-funcționale):</strong>{' '}
          Necesită consimțământ activ (opt-in) înainte de instalare.
        </li>
      </ul>
      <p className="mt-2">
        <strong>
          SSMPlatform folosește exclusiv cookies / storage din prima categorie — strict necesare.
        </strong>{' '}
        Nu folosim cookies de tracking, profilare comportamentală, publicitate sau marketing.
      </p>
    </Section>

    <Section title="3. Lista exhaustivă a tehnologiilor de stocare utilizate">
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs border border-slate-200 dark:border-slate-700">
          <thead>
            <tr className="bg-slate-100 dark:bg-slate-700">
              <th className="border border-slate-200 dark:border-slate-600 px-2 py-1 text-left">
                Cheie
              </th>
              <th className="border border-slate-200 dark:border-slate-600 px-2 py-1 text-left">
                Tehnologie
              </th>
              <th className="border border-slate-200 dark:border-slate-600 px-2 py-1 text-left">
                Categorie
              </th>
              <th className="border border-slate-200 dark:border-slate-600 px-2 py-1 text-left">
                Scop
              </th>
              <th className="border border-slate-200 dark:border-slate-600 px-2 py-1 text-left">
                Durată
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                <code>authToken</code>
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                localStorage
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Strict necesar
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Token JWT pentru menținerea sesiunii autentificate
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                8 ore sau până la logout
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                <code>user</code>
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                localStorage
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Strict necesar
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Cache local cu datele profilului (nume, rol, companyId)
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Până la logout sau modificarea datelor
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                <code>platforma_ssm_theme</code>
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                sessionStorage
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Funcțional (UX)
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Preferința dark/light pe sesiunea curentă
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Sesiune browser
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                <code>i18nextLng</code>
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                localStorage
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Funcțional
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Limba aleasă (RO / EN)
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Persistent până la ștergere manuală
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                <code>signing_master_key_*</code>
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                sessionStorage
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Strict necesar (securitate)
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Cheia derivată din parolă pentru semnătura AdES. Nu părăsește dispozitivul.
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Sesiune browser sau logout
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-2">
        <strong>Detalii tehnice:</strong>
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          Toate datele sunt stocate doar pe dispozitivul Utilizatorului, nu pe serverele
          Operatorului.
        </li>
        <li>
          <code>authToken</code> și <code>signing_master_key_*</code> sunt sensibile — nu le copiați
          și nu le transmiteți terților.
        </li>
        <li>
          Serverul Operatorului trimite cookies de sesiune doar în context strict tehnic (ex:
          protecție CSRF dacă este implementată).
        </li>
      </ul>
    </Section>

    <Section title="4. Cookies pe care NU le folosim">
      <p>Pentru transparență deplină, declarăm explicit că Platforma nu folosește:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          Cookies / pixeli de la Google Analytics, Google Tag Manager, Meta Pixel, TikTok Pixel sau
          orice alt furnizor de analitice publicitare.
        </li>
        <li>Cookies de remarketing (Google Ads, Facebook Ads).</li>
        <li>
          Cookies de profilare comportamentală (Hotjar, Mixpanel, Amplitude, Heap, FullStory etc.).
        </li>
        <li>Trackeri terți (third-party cookies).</li>
        <li>Cookies de rețele sociale (Like buttons, share widgets).</li>
      </ul>
      <p className="mt-2">
        Dacă, în viitor, Operatorul va introduce un instrument analitic (de exemplu Plausible,
        Matomo cu privacy mode, sau Google Analytics 4), această Politică va fi actualizată și se va
        implementa un banner de consimțământ conform CJUE Planet49, cu opt-in activ.
      </p>
    </Section>

    <Section title="5. Bază legală">
      <p>
        Pentru cookies / storage strict necesare: Art. 4 alin. 5 lit. b din Legea 506/2004 —
        stocarea este permisă fără consimțământ dacă este „strict necesară pentru furnizarea
        serviciului solicitat în mod expres de utilizator".
      </p>
      <p className="mt-2">
        <strong>Justificarea pentru fiecare element din tabelul de la pct. 3:</strong>
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          <code>authToken</code> și <code>user</code> — fără aceste valori, Utilizatorul ar trebui
          să introducă parola la fiecare pagină. Esențial pentru orice aplicație autentificată.
        </li>
        <li>
          <code>platforma_ssm_theme</code> și <code>i18nextLng</code> — funcțional, bazat pe
          acțiunea expresă a Utilizatorului care apasă comutatorul.
        </li>
        <li>
          <code>signing_master_key_*</code> — esențial pentru semnătura electronică, fără el
          Utilizatorul nu poate semna fișele SSM (obligație legală).
        </li>
      </ul>
    </Section>

    <Section title="6. Cum gestionați și ștergeți cookies">
      <h3 className="font-semibold mt-3">6.1. Din browser</h3>
      <ul className="list-disc pl-5 space-y-1">
        <li>Chrome: chrome://settings/cookies</li>
        <li>Firefox: about:preferences#privacy</li>
        <li>Edge: edge://settings/privacy</li>
        <li>Safari: Preferences → Privacy</li>
      </ul>
      <h3 className="font-semibold mt-3">6.2. Din Platformă</h3>
      <p>
        Utilizatorul se poate deconecta oricând prin butonul „Deconectare", ceea ce șterge automat{' '}
        <code>authToken</code>, <code>user</code> și <code>signing_master_key_*</code>. Preferințele
        de temă și limbă rămân stocate (utile la următoarea conectare).
      </p>
      <h3 className="font-semibold mt-3">6.3. Atenție: efectele ștergerii</h3>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          Ștergerea localStorage → veți fi deconectat automat și va trebui să introduceți din nou
          parola.
        </li>
        <li>
          Ștergerea sessionStorage (sau închiderea browserului) → cheia de semnătură se șterge; nu
          veți putea semna documente până la o nouă autentificare.
        </li>
        <li>Ștergerea preferințelor → Platforma revine la valorile implicite (RO + light mode).</li>
      </ul>
    </Section>

    <Section title="7. Cookies pentru protecția împotriva abuzurilor">
      <p>
        Pe lângă cookies-urile listate, Operatorul utilizează metode server-side (rate limiting
        bazat pe IP, audit log) care nu stochează cookies pe dispozitiv, dar care implică
        prelucrarea adresei IP în temeiul interesului legitim de securitate (Art. 6 alin. 1 lit. f
        GDPR, considerentul 49). Aceste prelucrări sunt detaliate în Politica de Confidențialitate.
      </p>
    </Section>

    <Section title="8. Modificarea acestei Politici">
      <p>
        Operatorul își rezervă dreptul de a actualiza Politica de Cookies. La modificări
        semnificative (în special introducerea de cookies non-esențiale), Utilizatorul va fi
        notificat și i se va cere consimțământ activ.
      </p>
    </Section>

    <Section title="9. Contact">
      <ul className="list-disc pl-5 space-y-1">
        <li>DPO: contact@razitech.ro</li>
        <li>Suport: contact@razitech.ro</li>
      </ul>
    </Section>
  </div>
);

const TermsContent = () => (
  <div className="prose prose-slate dark:prose-invert max-w-none text-sm text-slate-700 dark:text-slate-300 space-y-4">
    <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
      Termeni și Condiții de utilizare a Platformei SSM
    </h1>
    <p className="text-slate-500 dark:text-slate-400">
      <strong>Versiune:</strong> 2.0 &nbsp;·&nbsp; <strong>Data intrării în vigoare:</strong>{' '}
      08.07.2026
    </p>

    <Section title="1. Definiții">
      <p>În cuprinsul prezentului document, termenii de mai jos au următoarea semnificație:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          <strong>„Platforma"</strong> — aplicația software-as-a-service „SSMPlatform" accesibilă la
          adresa web a Operatorului, inclusiv bazele de date, conținutul, materialele de instruire
          și toate funcționalitățile aferente.
        </li>
        <li>
          <strong>„Operator" / „Furnizor"</strong> — RAZI TECH S.R.L., cu sediul în Jud. Brașov,
          Municipiul Brașov, Strada Jupiter, Nr. 4, Bl. 146, Scara A, Ap. 15, înregistrată la
          Registrul Comerțului sub nr. J2024030566002, având CUI 50690233, e-mail
          contact@razitech.ro.
        </li>
        <li>
          <strong>„Client"</strong> — persoana juridică (firmă SSM, angajator, organizație) care a
          contractat utilizarea Platformei pentru a-și gestiona obligațiile legale privind
          securitatea și sănătatea în muncă.
        </li>
        <li>
          <strong>„Utilizator"</strong> — orice persoană fizică ce accesează Platforma în baza unui
          cont creat de un Client (administrator, inspector SSM, angajat etc.).
        </li>
        <li>
          <strong>„Cont"</strong> — spațiul personal protejat prin credențiale unice (e-mail +
          parolă).
        </li>
        <li>
          <strong>„Servicii"</strong> — funcționalitățile puse la dispoziție de Operator: găzduirea
          conținutului de instruire, generarea fișelor individuale, evidența certificatelor,
          rapoarte, semnătură electronică, asistență tehnică.
        </li>
        <li>
          <strong>„Date personale"</strong> — orice informații privind o persoană fizică
          identificată sau identificabilă, conform Art. 4 pct. 1 din Regulamentul UE 2016/679
          (GDPR).
        </li>
      </ul>
    </Section>

    <Section title="2. Obiectul serviciului">
      <p>
        2.1. Platforma este un sistem de management al instruirilor de securitate și sănătate în
        muncă (SSM) și pentru situații de urgență (SU), destinat exclusiv mediului profesional, în
        conformitate cu Legea 319/2006, HG 1425/2006, Legea 307/2006 și legislația conexă.
      </p>
      <p>
        2.2. Operatorul furnizează Clientului acces la Platformă în regim Software-as-a-Service
        (SaaS), fără transfer de proprietate intelectuală asupra codului sursă, design-ului sau a
        conținutului standard de instruire elaborat de Operator.
      </p>
      <p>
        2.3. Operatorul nu este formator SSM și nu emite atestate ITM pe numele propriu. Conținutul
        de instruire utilizat pe Platformă este responsabilitatea Clientului, care își asumă
        valabilitatea legală a materialelor și a fișelor semnate pe Platformă.
      </p>
    </Section>

    <Section title="3. Crearea și gestionarea conturilor">
      <p>
        3.1. <strong>Cont de administrator (Client).</strong> Crearea contului de administrator se
        face exclusiv în baza unei comenzi/contract semnate cu Operatorul, prin procedură asistată.
        Operatorul își rezervă dreptul de a refuza crearea contului pentru entități care nu
        îndeplinesc condițiile contractuale sau de risc.
      </p>
      <p>
        3.2. <strong>Conturi subordonate.</strong> Administratorul Clientului are dreptul și
        obligația de a crea conturi pentru angajații proprii sau ai firmelor partenere, asumându-și
        calitatea de operator de date pentru acestea, conform GDPR.
      </p>
      <p>
        3.3. <strong>Credențiale.</strong> La crearea contului, Utilizatorul primește o parolă
        temporară care trebuie schimbată la prima conectare. Operatorul nu deține parolele în clar —
        acestea sunt stocate sub formă de hash criptografic (bcrypt, factor 12).
      </p>
      <p>
        3.4. <strong>Răspunderea utilizatorului.</strong> Utilizatorul este răspunzător pentru toate
        operațiunile efectuate cu credențialele sale. Semnăturile electronice aplicate sub
        identitatea unui Utilizator au valoarea juridică prevăzută de Regulamentul (UE) 910/2014
        (eIDAS) pentru semnătura electronică avansată (AdES).
      </p>
    </Section>

    <Section title="4. Roluri și permisiuni">
      <p>4.1. Platforma utilizează 6 niveluri ierarhice de acces:</p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs border border-slate-200 dark:border-slate-700">
          <thead>
            <tr className="bg-slate-100 dark:bg-slate-700">
              <th className="border border-slate-200 dark:border-slate-600 px-2 py-1 text-left">
                Rol
              </th>
              <th className="border border-slate-200 dark:border-slate-600 px-2 py-1 text-left">
                Atribuții principale
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                SUPER_ADMIN
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Administrare globală platformă (rezervat Operatorului)
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                SSM_COMPANY_ADMIN
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Administrarea unei firme SSM externe + firmelor client gestionate
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                SSM_INSTRUCTOR
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Conducerea instruirilor, semnătură instructor (cu opțiune PSI)
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                COMPANY_ADMIN
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Administrarea unei firme angajator
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                DIRECTOR_GENERAL
              </td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Vizualizare globală + semnătură conducător unitate
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">EMPLOYEE</td>
              <td className="border border-slate-200 dark:border-slate-600 px-2 py-1">
                Acces la propriile instruiri, semnătură proprie
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-2">
        4.2. <strong>Sub-flag-uri:</strong> Utilizatorii pot avea atribute suplimentare (CLM —
        conducător loc muncă; HR — resurse umane; responsabil semnătură).
      </p>
      <p>
        4.3. Atribuirea și revocarea rolurilor este în responsabilitatea administratorului
        Clientului. Operatorul nu intervine în această configurație.
      </p>
    </Section>

    <Section title="5. Obligațiile Utilizatorului">
      <p>5.1. Utilizatorul se obligă:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          să furnizeze date corecte, complete și actualizate (nume, prenume, CNP, traseu
          casă-serviciu etc.);
        </li>
        <li>să nu permită accesul terților la contul propriu;</li>
        <li>
          să nu folosească Platforma în scopuri ilegale, fraudule, ofensatoare sau contrare bunelor
          moravuri;
        </li>
        <li>
          să nu încerce să compromită securitatea Platformei (atacuri, injecții, scraping nepermis);
        </li>
        <li>să respecte drepturile de autor asupra conținutului de instruire;</li>
        <li>
          să semneze documentele în mod conștient și după verificarea conținutului, asumându-și
          răspunderea conform Art. 326 Cod Penal (fals în declarații).
        </li>
      </ul>
      <p>
        5.2. <strong>Declarații pe propria răspundere.</strong> Semnarea fișelor SSM/SU echivalează
        cu o declarație pe propria răspundere a Utilizatorului că a luat la cunoștință conținutul
        instruirii și că datele sale personale sunt corecte.
      </p>
    </Section>

    <Section title="6. Obligațiile Operatorului">
      <p>6.1. Operatorul se obligă:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          să asigure disponibilitate de minim 99% lunar a Platformei (exclusiv mentenanțele
          programate anunțate cu cel puțin 48 h în avans);
        </li>
        <li>
          să implementeze măsuri tehnice și organizatorice de securitate adecvate (criptare, control
          acces, audit log, backup zilnic);
        </li>
        <li>
          să păstreze copii de siguranță zilnice ale bazei de date, retenționate minim 30 zile;
        </li>
        <li>
          să notifice Clientul în termen de 72 ore despre orice incident de securitate care
          afectează datele personale (Art. 33 GDPR);
        </li>
        <li>să răspundă solicitărilor de suport tehnic în maxim 2 zile lucrătoare;</li>
        <li>
          să nu acceseze datele Clientului decât în scopuri tehnice strict necesare, conform
          regimului de persoană împuternicită prevăzut de Art. 28 GDPR.
        </li>
      </ul>
      <p>
        6.2. <strong>Mentenanță programată.</strong> Operatorul își rezervă dreptul de a efectua
        mentenanțe programate, de preferință în afara orelor de program (22:00 - 06:00), cu
        notificare prealabilă.
      </p>
    </Section>

    <Section title="7. Modalitatea de plată">
      <p>
        7.1. <strong>Facturare manuală.</strong> Platforma nu integrează plăți în-app. Plata
        serviciilor se face în baza facturilor emise de Operator, conform contractului semnat cu
        Clientul, prin virament bancar în termen de 15 zile de la data emiterii.
      </p>
      <p>
        7.2. <strong>Tarifare.</strong> Tarifele se stabilesc individual prin contract, pot fi
        raportate la numărul de utilizatori activi, la numărul de firme client gestionate sau pe
        alte criterii agreate.
      </p>
      <p>
        7.3. <strong>Întârziere plată.</strong> În cazul neachitării facturilor în termenul de
        scadență:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Operatorul transmite o notificare de restanță;</li>
        <li>
          după 30 zile suplimentare de întârziere, Operatorul își rezervă dreptul de a suspenda
          accesul la Platformă;
        </li>
        <li>
          după 60 zile, Operatorul poate rezilia contractul cu reținerea dreptului la
          daune-interese. Datele Clientului rămân arhivate încă 90 zile în vederea exportului final
          (conform pct. 10).
        </li>
      </ul>
      <p>
        7.4. <strong>TVA și fiscalitate.</strong> Toate prețurile sunt exprimate fără TVA, dacă nu
        se specifică altfel. Operatorul emite facturi conform legislației fiscale române.
      </p>
    </Section>

    <Section title="8. Proprietate intelectuală">
      <p>
        8.1. <strong>Platforma</strong> (cod, design, baze de date, conținut standard) este
        proprietatea exclusivă a Operatorului și este protejată conform Legii 8/1996 privind
        drepturile de autor.
      </p>
      <p>
        8.2. <strong>Conținutul Clientului</strong> (cursuri proprii, sigle, documente uploadate)
        rămâne proprietatea Clientului. Prin utilizarea Platformei, Clientul acordă Operatorului o
        licență neexclusivă, limitată la durata contractului, exclusiv în scopul furnizării
        Serviciilor.
      </p>
      <p>
        8.3. <strong>Marca „SSMPlatform"</strong> și logo-ul aferent sunt protejate. Utilizarea lor
        în scopuri comerciale fără acord scris este interzisă.
      </p>
    </Section>

    <Section title="9. Confidențialitate și protecția datelor">
      <p>
        9.1. Operatorul prelucrează datele personale ale Utilizatorilor în calitate de persoană
        împuternicită (Art. 28 GDPR) acționând în numele Clientului, care este operator în sensul
        GDPR.
      </p>
      <p>9.2. Detaliile concrete privind prelucrarea datelor sunt reglementate prin:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          Politica de Confidențialitate publicată la <code>/legal/privacy</code>;
        </li>
        <li>Acordul de prelucrare a datelor (DPA) semnat separat cu fiecare Client.</li>
      </ul>
      <p>
        9.3. Operatorul a desemnat un Responsabil cu Protecția Datelor (DPO), contactabil la
        contact@razitech.ro.
      </p>
    </Section>

    <Section title="10. Exportul datelor și exit-ul Clientului">
      <p>
        10.1. La încetarea contractului, Clientul are dreptul de a primi o copie a tuturor datelor
        stocate pe Platformă în format structurat (JSON / CSV / PDF arhivate), conform Art. 28 alin.
        (3) lit. (g) GDPR.
      </p>
      <p>
        10.2. Cererea de export trebuie formulată în scris către contact@razitech.ro în termen de 90
        zile de la încetare. După acest termen, Operatorul poate șterge datele, cu excepția celor pe
        care legea (HG 1425/2006, dreptul fiscal etc.) impune să fie păstrate pentru durate mai
        îndelungate.
      </p>
      <p>
        10.3. <strong>Anonimizare.</strong> Datele care trebuie păstrate conform legii pot fi
        anonimizate (eliminarea identificatorilor direcți) pentru a respecta principiul minimizării
        (Art. 5 GDPR).
      </p>
    </Section>

    <Section title="11. Limitarea răspunderii">
      <p>
        11.1. Operatorul depune toate diligențele rezonabile pentru funcționarea Platformei, dar nu
        garantează că Serviciile vor fi neîntrerupte, fără erori sau perfect adaptate nevoilor
        specifice ale fiecărui Client.
      </p>
      <p>
        11.2. <strong>Cap de răspundere.</strong> Răspunderea totală a Operatorului față de Client,
        pentru orice cauză (contractuală sau delictuală), este limitată la echivalentul sumei
        plătite de Client în ultimele 12 luni anterioare evenimentului dăunător.
      </p>
      <p>
        11.3. <strong>Excluderi.</strong> Operatorul nu răspunde pentru:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>daune indirecte, pierderi de profit, pierderi de oportunitate;</li>
        <li>conținutul cursurilor încărcate de Client;</li>
        <li>
          corectitudinea juridică a fișelor SSM/SU semnate (responsabilitatea revine firmei SSM
          și/sau angajatorului);
        </li>
        <li>
          comportamentul Utilizatorilor finali (ex: completare incorectă a CNP, semnarea unor
          documente neînțelese);
        </li>
        <li>
          forța majoră (Art. 1.351 Cod Civil) și cazul fortuit (atacuri DDoS masive, defecțiuni
          majore la furnizorii de infrastructură, hotărâri ale autorităților, război, pandemii).
        </li>
      </ul>
      <p>
        11.4. <strong>Limitări neaplicabile.</strong> Limitările de răspundere de mai sus nu se
        aplică în caz de:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>dol sau culpă gravă a Operatorului;</li>
        <li>încălcarea obligațiilor esențiale de securitate care a condus la o breșă de date;</li>
        <li>vătămare adusă persoanelor.</li>
      </ul>
    </Section>

    <Section title="12. Suspendare și reziliere">
      <p>
        12.1. <strong>Suspendare imediată.</strong> Operatorul poate suspenda accesul Utilizatorului
        fără preaviz în cazul:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>compromiterii securității contului;</li>
        <li>folosirii abuzive a Platformei (atacuri, scraping, spam);</li>
        <li>suspiciunii de fraudă sau acțiuni ilegale.</li>
      </ul>
      <p>
        12.2. <strong>Reziliere de către Client.</strong> Clientul poate rezilia contractul cu
        preaviz de 30 zile transmis în scris la contact@razitech.ro.
      </p>
      <p>
        12.3. <strong>Reziliere de către Operator.</strong> Operatorul poate rezilia contractul: (a)
        cu preaviz 60 zile, fără justificare; (b) imediat, în caz de încălcare gravă de către
        Client.
      </p>
      <p>
        12.4. <strong>Ștergerea contului.</strong> La cererea Utilizatorului persoană fizică (Art.
        17 GDPR), contul este dezactivat și datele anonimizate, cu excepția celor a căror păstrare e
        impusă de lege (vezi Politica de Confidențialitate, pct. retenție).
      </p>
    </Section>

    <Section title="13. Forța majoră">
      <p>
        Niciuna dintre Părți nu va fi răspunzătoare pentru neîndeplinirea obligațiilor în caz de
        forță majoră dovedită conform legii române (Art. 1.351 Cod Civil), cu condiția notificării
        celeilalte Părți în termen de 5 zile lucrătoare.
      </p>
    </Section>

    <Section title="14. Modificarea termenilor">
      <p>
        14.1. Operatorul își rezervă dreptul de a actualiza prezentele Termeni. Versiunea curentă
        este disponibilă în permanență la <code>/legal/terms</code>.
      </p>
      <p>
        14.2. <strong>Modificări semnificative</strong> (preț, scop, drepturi-obligații esențiale)
        sunt notificate cu 30 zile în avans prin e-mail la administratorul Clientului și prin
        afișare în Platformă. Continuarea utilizării după acest termen echivalează cu acceptarea
        noilor termeni.
      </p>
      <p>
        14.3. <strong>Versiunea politicii de confidențialitate</strong> este controlată separat prin
        câmpul <code>gdprVersion</code>; la modificarea Politicii, Utilizatorul va fi rugat să
        accepte din nou la următoarea conectare.
      </p>
    </Section>

    <Section title="15. Comunicări">
      <p>
        15.1. Toate notificările între Părți se transmit la adresele de e-mail înregistrate,
        considerate adrese juridice valabile pentru comunicare conform Legii 365/2002 privind
        comerțul electronic.
      </p>
      <p>
        15.2. Adresa oficială a Operatorului pentru orice cerere scrisă: contact@razitech.ro și/sau
        Jud. Brașov, Municipiul Brașov, Strada Jupiter, Nr. 4, Bl. 146, Scara A, Ap. 15.
      </p>
    </Section>

    <Section title="16. Legea aplicabilă și jurisdicție">
      <p>
        16.1. Prezenții Termeni sunt guvernați de legea română. Părțile se obligă să încerce
        soluționarea oricărui litigiu pe cale amiabilă în termen de 30 zile.
      </p>
      <p>
        16.2. În absența unei soluții amiabile, litigiile vor fi soluționate de către instanțele
        judecătorești competente din municipiul Brașov.
      </p>
      <p>
        16.3. Pentru Utilizatorul persoană fizică acționând în afara unei activități profesionale,
        se aplică dreptul consumatorului — instanța competentă este cea de la domiciliul
        Utilizatorului.
      </p>
    </Section>

    <Section title="17. Dispoziții finale">
      <p>
        17.1. <strong>Nulitate parțială.</strong> Dacă o clauză din prezentele Termeni este
        declarată nulă, restul clauzelor rămân valabile.
      </p>
      <p>
        17.2. <strong>Renunțarea la un drept.</strong> Tolerarea unei încălcări de către o Parte nu
        echivalează cu renunțarea la drepturile sale.
      </p>
      <p>
        17.3. <strong>Cesiune.</strong> Drepturile și obligațiile Clientului nu pot fi cesionate
        fără acordul prealabil scris al Operatorului. Operatorul poate cesiona contractul în caz de
        reorganizare a societății, cu notificare prealabilă.
      </p>
      <p>
        17.4. <strong>Limba.</strong> Versiunea oficială a prezentelor Termeni este cea în limba
        română. Traducerile (engleză etc.) sunt orientative.
      </p>
    </Section>
  </div>
);

const AboutContent = () => (
  <div className="prose prose-slate dark:prose-invert max-w-none text-sm text-slate-700 dark:text-slate-300 space-y-4">
    <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Despre platformă</h1>
    <p>
      <strong>SSMPlatform</strong> e un sistem de management al instruirilor de securitate și
      sănătate în muncă, conform Legii 319/2006 și H.G. 1425/2006, plus instruirilor pentru situații
      de urgență (Legea 307/2006).
    </p>

    <Section title="Ce face platforma">
      <ul className="list-disc pl-5 space-y-1">
        <li>Atribuire și organizare cursuri SSM/SU</li>
        <li>Module video cu întrebări interactive</li>
        <li>Test final cu scor automat</li>
        <li>Generare fișe individuale și certificate (PDF)</li>
        <li>Semnare digitală cu validare temporală</li>
        <li>Evidență instruiri periodice (IP) cu reminder-e</li>
        <li>Rapoarte ITM</li>
        <li>Multi-tenant: firme SSM ↔ firme client</li>
      </ul>
    </Section>

    <Section title="Tehnologii">
      <ul className="list-disc pl-5 space-y-1">
        <li>Frontend: React 18, Tailwind CSS</li>
        <li>Backend: Node.js, Express, Prisma ORM</li>
        <li>Bază de date: PostgreSQL</li>
        <li>Generare PDF: pdfkit</li>
        <li>Securitate: bcrypt, AES-256-GCM, JWT, helmet</li>
      </ul>
    </Section>
  </div>
);

const ContactContent = () => (
  <div className="prose prose-slate dark:prose-invert max-w-none text-sm text-slate-700 dark:text-slate-300 space-y-4">
    <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Contact și suport</h1>
    <p>
      Pentru întrebări legate de funcționarea platformei sau de datele dumneavoastră personale,
      contactați-ne pe canalele de mai jos.
    </p>

    <Section title="Suport tehnic">
      <p>
        Email:{' '}
        <a href="mailto:contact@razitech.ro" className="text-primary-600 hover:underline">
          contact@razitech.ro
        </a>
      </p>
      <p>Răspuns: în maxim 2 zile lucrătoare</p>
    </Section>

    <Section title="Probleme cu datele personale (GDPR)">
      <p>Email DPO: contact@razitech.ro</p>
      <p>
        Pentru exercitarea drepturilor (acces, rectificare, ștergere etc.), trimiteți o cerere
        scrisă din contul de email asociat cu contul de pe platformă.
      </p>
    </Section>

    <Section title="Plângeri / autorități">
      <p>
        ANSPDCP (Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal):{' '}
        <a
          href="https://www.dataprotection.ro"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:underline"
        >
          www.dataprotection.ro
        </a>
      </p>
    </Section>
  </div>
);

const Section = ({ title, children }) => (
  <section>
    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mt-6 mb-2">{title}</h2>
    {children}
  </section>
);

export default Legal;
